import { formatAmount, toBaseUnits, type TokenScale } from "@/lib/format/amount";
import { amountError, parseTypedAmount } from "./amount";

/* What a withdrawal form is allowed to send.

   Every limit here comes from `GET /withdraw/{routeId}` or from the balance —
   none of it is a house rule. The point is to say no *before* the request, so
   the player reads the minimum while they are still typing rather than a
   rejected 400 after they pressed the button. The API enforces all of it again;
   this only decides what is worth sending. The messages are the whitelabel's
   (WithdrawToAddressForm + amountValidation).

   Units: a withdrawal is taken from the bankroll balance, and the platform
   compares `amount` against that balance and against the route's
   `minimumAmount` as plain integers (transfer service, withdraw.ts and
   validate-withdrawal.ts) — so the amount, the minimum and the balance are all
   at the **bankroll token's** `decimals`, never the route's destination token.
   The whitelabel parses with `bankroll.token.decimals` for the same reason. */

export type WithdrawalLimits = {
  /* A regular expression, as a string, that a destination address must match
     on this route's network. */
  addressPattern: string;
  /* Base units of the bankroll token. */
  minimumAmount: string;
  balance: string;
  /* The bankroll token: the scale every figure above is in. */
  token: TokenScale & { symbol: string };
};

export type WithdrawalErrors = { address: string | null; amount: string | null };

/* The pattern is the API's, not ours, and a broken one must not take the form
   down with it — an address that cannot be checked is passed through for the
   platform to judge. */
const matchesPattern = (address: string, pattern: string) => {
  try {
    return new RegExp(pattern).test(address);
  } catch {
    return true;
  }
};

/* Both fields are required, so an empty one carries a message too; the form
   decides whether it has been touched enough to show it. */
export const validateWithdrawal = (
  { address, amount }: { address: string; amount: string },
  { addressPattern, minimumAmount, balance, token }: WithdrawalLimits,
): WithdrawalErrors => ({
  address: addressError(address, addressPattern),
  amount:
    amount.trim().length === 0
      ? "Please enter an amount."
      : amountError(amount, {
          minAmount: toBaseUnits(minimumAmount),
          maxAmount: toBaseUnits(balance),
          decimals: token.decimals,
          significantDecimals: token.significantDecimals,
          actionLabel: "withdrawal",
        }),
});

const addressError = (address: string, pattern: string) => {
  const trimmed = address.trim();

  if (trimmed.length === 0) {
    return "Please enter a wallet address.";
  }

  return matchesPattern(trimmed, pattern) ? null : "Please enter a valid wallet address.";
};

/* Whether the form may be submitted at all: nothing wrong with either field,
   empty included. */
export const canWithdraw = (errors: WithdrawalErrors) => errors.address === null && errors.amount === null;

/* The typed amount as `POST /withdraw/{routeId}` takes it: an integer string in
   the bankroll token's base units. */
export const withdrawalAmount = (amount: string, token: TokenScale) => parseTypedAmount(amount, token.decimals);

/* Money tied to unfinished wagering — an active bonus tranche, or the AML
   turnover on a deposit. Informational only: the platform pays out the part of
   the balance that is not locked and refuses the rest with a reason that names
   what is left to wager, so the form does not second-guess it (the whitelabel
   does not gate on these either). */
export const wageringHint = ({
  trancheLockedAmount,
  amlLockedAmount,
  token,
}: {
  trancheLockedAmount: string;
  amlLockedAmount: string;
  token: WithdrawalLimits["token"];
}): string | null => {
  const locked = toBaseUnits(trancheLockedAmount) + toBaseUnits(amlLockedAmount);

  if (locked <= 0n) {
    return null;
  }

  return `${formatAmount(locked.toString(), token, { rounding: "up" })} ${token.symbol} of this balance is tied to wagering that is not finished yet. The rest can be withdrawn.`;
};

/* The whitelabel's mustVerifyEmailBeforeWithdrawal. `/site` carries the
   policy; the API publishes no "is this email verified" flag, so the one thing
   that can be stated for certain is that an account with no email at all
   cannot clear it. An unread profile is not a reason to stop anyone. */
export const mustVerifyEmailBeforeWithdrawal = ({
  policies,
  profile,
}: {
  policies: { requireEmailVerificationForWithdrawal: boolean } | undefined;
  profile: { email: string | null } | undefined;
}) =>
  profile !== undefined && policies?.requireEmailVerificationForWithdrawal === true && profile.email === null;
